import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  ArrowLeft,
  ExternalLink,
  LifeBuoy,
  Mail,
  Scale,
  ShieldCheck,
  Trash2
} from 'lucide-react';
import PublicFooter from '../components/PublicFooter';

const SUPPORT_EMAIL = 'aaron@TexanTitle.com';
const COMPANY_ADDRESS = '6710 Stewart Road, Suite 300, Galveston, Texas 77551';

const Section = ({ title, children }) => (
  <section className="border-t border-gray-200 pt-7 first:border-t-0 first:pt-0">
    <h2 className="text-xl font-bold text-primary-700 sm:text-2xl">{title}</h2>
    <div className="mt-3 space-y-4 text-[15px] leading-7 text-gray-700 sm:text-base">
      {children}
    </div>
  </section>
);

const BulletList = ({ children }) => (
  <ul className="list-disc space-y-2 pl-6 marker:text-secondary-500">{children}</ul>
);

const ContactBlock = ({ subject = 'TTIC Marketing Hub question' }) => (
  <div className="rounded-xl border border-primary-100 bg-primary-50 p-5">
    <p className="font-semibold text-primary-800">Texan Title Insurance Company</p>
    <a
      className="mt-2 inline-flex items-center gap-2 font-semibold text-primary-600 hover:text-primary-800 hover:underline"
      href={`mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(subject)}`}
    >
      <Mail size={17} aria-hidden="true" />
      {SUPPORT_EMAIL}
    </a>
    <p className="mt-2 text-sm text-gray-600">{COMPANY_ADDRESS}</p>
  </div>
);

const PrivacyContent = () => (
  <>
    <Section title="Overview">
      <p>
        This Privacy Policy explains how Texan Title Insurance Company (“TTIC,” “we,” “us,” or “our”)
        collects, uses, discloses, and protects information through the TTIC Marketing Hub at
        marketing.ttic.com (the “Hub”). The Hub is a business service for authorized title-company
        personnel and TTIC administrators.
      </p>
    </Section>

    <Section title="Information we collect">
      <BulletList>
        <li><strong>Account and profile information:</strong> name, business email, company, phone number, business address, website, social handles, account status, and assigned TTIC administrator.</li>
        <li><strong>Branding and content:</strong> company logos, selected brand colors, and flyer source files submitted by authorized administrators.</li>
        <li><strong>Security and device information:</strong> password hashes, sign-in dates, IP address, browser or device information, password-reset activity, and security records.</li>
        <li><strong>Hub activity:</strong> pages visited and time spent, flyer views, caption copies, downloads, email exports, shares, social publications, selected formats, and related timestamps.</li>
        <li><strong>Connected social accounts:</strong> if direct publishing is enabled and you choose to connect an account, we receive the account identifier, account name and type, authorization expiration, limited platform metadata, and an access token. Access tokens are encrypted before storage.</li>
        <li><strong>Communications:</strong> information included in support, privacy, or data-deletion requests.</li>
      </BulletList>
    </Section>

    <Section title="How we use information">
      <BulletList>
        <li>Operate, secure, maintain, and improve the Hub.</li>
        <li>Authenticate users and provide password-reset and account-support services.</li>
        <li>Apply a user’s logo, colors, business details, and optional social handles to marketing materials.</li>
        <li>Generate, download, email, share, or publish content when a user requests those actions.</li>
        <li>Provide assigned TTIC administrators with account, activity, engagement, and monthly usage reporting for the users they manage.</li>
        <li>Detect abuse, troubleshoot problems, comply with law, and protect users, TTIC, and others.</li>
      </BulletList>
    </Section>

    <Section title="When information is disclosed">
      <p>We do not sell personal information or use it for third-party targeted advertising. Information may be disclosed only as reasonably necessary:</p>
      <BulletList>
        <li>To TTIC personnel and the administrator assigned to manage your account.</li>
        <li>To vendors that provide hosting, database, file storage, email delivery, security, backup, and similar operational services under appropriate obligations.</li>
        <li>To LinkedIn, Meta, Facebook, or Instagram when you expressly connect an account or direct the Hub to publish content.</li>
        <li>To comply with law, legal process, or a valid governmental request; to protect legal rights or safety; or in connection with a merger, financing, acquisition, or sale of relevant business assets.</li>
      </BulletList>
    </Section>

    <Section title="Social-platform connections">
      <p>
        Connecting a social account is optional. The Hub requests only the permissions needed to identify
        the selected account and publish content at your direction. We do not ask for or store your social-platform
        password. You can disconnect a social account from your Hub profile, and you may also revoke the Hub’s
        access from the platform’s own account settings.
      </p>
      <p>
        Your use of a social platform remains subject to that platform’s terms and privacy practices. TTIC does not
        control how a third-party platform processes information after you direct the Hub to send content to it.
      </p>
    </Section>

    <Section title="Storage, retention, and security">
      <p>
        We retain account and activity information for as long as reasonably needed to provide the Hub, support
        administrator reporting, maintain security, resolve disputes, and meet legal obligations. Short-lived social
        publishing files are designed to expire after approximately 15 minutes. When an account is deleted, active
        account records, connected-social credentials, and the stored logo are removed; backup copies may remain
        for up to 30 days before automatic expiration, unless a longer period is required by law or necessary for security.
      </p>
      <p>
        We use safeguards appropriate to the nature of the information, including encrypted network connections,
        hashed passwords, encrypted social access tokens, access controls, and tested backups. No online service can
        guarantee absolute security.
      </p>
    </Section>

    <Section title="Your choices and rights">
      <BulletList>
        <li>Review or update profile, branding, and optional social-handle information in the Hub.</li>
        <li>Disconnect a linked social account at any time.</li>
        <li>Request access, correction, or deletion by contacting us below. Identity verification may be required.</li>
        <li>See the <Link className="font-semibold text-primary-600 hover:underline" to="/data-deletion">Data Deletion Instructions</Link> for the specific deletion process.</li>
      </BulletList>
    </Section>

    <Section title="Children’s privacy">
      <p>The Hub is a business service and is not intended for anyone under 18. We do not knowingly collect personal information from children.</p>
    </Section>

    <Section title="Changes and contact">
      <p>We may update this policy as the Hub changes. The effective date shown on this page will identify the latest version. Material changes may also be communicated through the Hub or by email.</p>
      <ContactBlock subject="TTIC Marketing Hub privacy question" />
    </Section>
  </>
);

const TermsContent = () => (
  <>
    <Section title="Agreement and eligibility">
      <p>
        These Terms of Use govern access to the TTIC Marketing Hub. By creating an account or using the Hub,
        you agree to these terms. The Hub is available only to authorized business users who are at least 18 years old
        and who have a valid invitation or TTIC administrative authorization.
      </p>
    </Section>

    <Section title="Accounts and access">
      <BulletList>
        <li>Provide accurate business and contact information and keep it current.</li>
        <li>Protect your password and account access. Do not share an account or allow unauthorized use.</li>
        <li>Notify TTIC promptly if you suspect unauthorized access.</li>
        <li>TTIC may suspend or terminate access to protect the Hub, enforce these terms, address inactivity or misuse, or end a user’s authorization.</li>
      </BulletList>
    </Section>

    <Section title="Permitted use of marketing materials">
      <p>
        Subject to these terms and any notices included in a flyer, TTIC grants authorized users a limited,
        nonexclusive, nontransferable, revocable right to customize, download, and use Hub materials in the ordinary
        marketing activities of their title company. Required TTIC underwriter marks, legal notices, attribution,
        and protected content may not be removed or misrepresented.
      </p>
      <p>You may not resell the Hub, redistribute editable templates, copy the flyer library into another service, interfere with the Hub, bypass access controls, introduce malicious material, or use the Hub unlawfully.</p>
    </Section>

    <Section title="Your logos and information">
      <p>
        You retain your rights in logos and other materials you submit. You represent that you have authority to use
        them and grant TTIC the limited permission necessary to store, process, resize, display, and place them into
        requested marketing materials. You are responsible for reviewing each finished item for accuracy before using it.
      </p>
    </Section>

    <Section title="Educational content—not professional advice">
      <p>
        Hub materials are general marketing and educational resources. They are not a title commitment, policy,
        legal opinion, underwriting decision, or legal, tax, financial, or insurance advice. Laws, forms, rates,
        underwriting requirements, and transaction facts may change. Users must confirm that selected content is
        current, appropriate, and accurate before distribution and should direct professional questions to qualified counsel or the appropriate TTIC department.
      </p>
    </Section>

    <Section title="Social publishing and third-party services">
      <p>
        If direct social publishing is offered, connecting an account is optional. You authorize the Hub to act only
        when you request a connection or publication. You remain responsible for the caption, audience, rights,
        regulatory compliance, and final post. LinkedIn, Meta, Facebook, Instagram, email providers, and other
        third-party services operate under their own terms and may change, restrict, or interrupt their services.
      </p>
    </Section>

    <Section title="Availability and disclaimers">
      <p>
        We work to keep the Hub reliable and secure, but it may occasionally be unavailable or contain an error.
        To the extent permitted by law, the Hub is provided “as is” and “as available,” without warranties of
        uninterrupted availability or fitness for a particular purpose. TTIC is not responsible for changes or outages
        caused by third-party platforms or for a user’s failure to review exported content before use.
      </p>
    </Section>

    <Section title="Responsibility and limits">
      <p>
        To the extent permitted by law, TTIC will not be liable for indirect, incidental, special, consequential,
        or punitive damages arising from use of the Hub. Nothing in these terms excludes liability that cannot legally
        be excluded. You are responsible for losses arising from your unlawful use, content you lacked permission to use,
        or violation of these terms.
      </p>
    </Section>

    <Section title="Changes, governing law, and contact">
      <p>
        We may update the Hub and these terms. Continued use after revised terms become effective constitutes acceptance
        of the revised terms. Texas law governs these terms, without regard to conflict-of-law principles, except where
        applicable law requires otherwise.
      </p>
      <ContactBlock subject="TTIC Marketing Hub terms question" />
    </Section>
  </>
);

const DataDeletionContent = () => (
  <>
    <Section title="Disconnect a social account">
      <p>To remove a connected LinkedIn, Facebook, or Instagram account from the Hub:</p>
      <ol className="list-decimal space-y-2 pl-6 marker:font-semibold marker:text-secondary-500">
        <li>Sign in to the TTIC Marketing Hub.</li>
        <li>Open <strong>My Branding</strong> or <strong>Profile</strong>.</li>
        <li>Find <strong>Connected Social Accounts</strong> and choose <strong>Disconnect</strong> for the applicable platform.</li>
      </ol>
      <p>
        Disconnecting deletes the Hub’s stored connection and encrypted access token. For an additional platform-side
        revocation, remove TTIC Marketing Hub from the connected apps or business integrations section of the relevant
        LinkedIn or Meta account.
      </p>
    </Section>

    <Section title="Request complete account deletion">
      <p>To delete your Hub account and associated data, send an email from the address registered to your account:</p>
      <div className="rounded-xl border border-secondary-100 bg-secondary-50 p-5">
        <p><strong>To:</strong> <a className="font-semibold text-primary-600 hover:underline" href={`mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent('Marketing Hub Data Deletion Request')}`}>{SUPPORT_EMAIL}</a></p>
        <p><strong>Subject:</strong> Marketing Hub Data Deletion Request</p>
        <p><strong>Include:</strong> your name, company name, registered email address, and whether any social accounts are connected.</p>
      </div>
      <p>
        We may request reasonable information to verify that you control the account. Once verified, TTIC will remove
        the active account and associated profile, branding, stored logo, activity records, exports, sessions,
        password-reset records, and connected-social credentials, subject to the exceptions below.
      </p>
    </Section>

    <Section title="What may remain temporarily">
      <p>
        Encrypted backup copies may remain for up to 30 days before automatic expiration. We may retain limited records
        for longer when reasonably necessary to comply with law, resolve a dispute, prevent fraud or abuse, or establish
        or defend legal claims. Information retained for these purposes will not be used for ordinary Hub activity.
      </p>
      <p>
        Deleting data from the Hub does not delete posts already published to a social platform. Those posts must be
        deleted directly from the applicable social account.
      </p>
    </Section>

    <Section title="Need help?">
      <ContactBlock subject="Marketing Hub Data Deletion Request" />
    </Section>
  </>
);

const SupportContent = () => (
  <>
    <Section title="How we can help">
      <BulletList>
        <li>Invite codes, account setup, sign-in access, and password resets.</li>
        <li>Logo uploads, suggested colors, contact information, and social handles.</li>
        <li>Flyer previews, downloads, email delivery, formatting, and print quality.</li>
        <li>Connecting, disconnecting, or troubleshooting supported social accounts.</li>
        <li>Privacy questions and requests to access, correct, or delete account data.</li>
      </BulletList>
    </Section>

    <Section title="Contact Marketing Hub support">
      <ContactBlock subject="TTIC Marketing Hub support request" />
      <p>
        For the fastest help, include your name, company, registered email address, the flyer or page involved,
        and a description or screenshot of what happened. Never send your password, password-reset link, social-platform
        access token, or another confidential credential.
      </p>
    </Section>

    <Section title="Account-security concerns">
      <p>
        If you believe someone accessed your account without permission, reset your password from the sign-in page and
        contact support immediately. An administrator can suspend access while the issue is reviewed.
      </p>
    </Section>

    <Section title="Other TTIC resources">
      <a
        className="inline-flex items-center gap-2 font-semibold text-primary-600 hover:text-primary-800 hover:underline"
        href="https://ttic.com"
        target="_blank"
        rel="noreferrer"
      >
        Visit TTIC.com
        <ExternalLink size={16} aria-hidden="true" />
      </a>
      <p>Marketing Hub support does not replace underwriting, claims, policy, or transaction-specific assistance.</p>
    </Section>
  </>
);

const pageDefinitions = {
  '/privacy': {
    title: 'Privacy Policy',
    description: 'How the TTIC Marketing Hub collects, uses, and protects information.',
    Icon: ShieldCheck,
    content: <PrivacyContent />
  },
  '/terms': {
    title: 'Terms of Use',
    description: 'The rules and responsibilities that apply when using the TTIC Marketing Hub.',
    Icon: Scale,
    content: <TermsContent />
  },
  '/data-deletion': {
    title: 'Data Deletion Instructions',
    description: 'How to disconnect a social account or request deletion of your Marketing Hub account and data.',
    Icon: Trash2,
    content: <DataDeletionContent />
  },
  '/support': {
    title: 'Support',
    description: 'Get help with your account, branding, flyers, or connected social accounts.',
    Icon: LifeBuoy,
    content: <SupportContent />
  }
};

const PublicInformation = () => {
  const location = useLocation();
  const page = pageDefinitions[location.pathname] || pageDefinitions['/support'];
  const Icon = page.Icon;

  useEffect(() => {
    document.title = `${page.title} | TTIC Marketing Hub`;
    const description = document.querySelector('meta[name="description"]');
    if (description) description.setAttribute('content', page.description);

    return () => {
      document.title = 'TTIC Marketing Hub';
      if (description) description.setAttribute('content', 'TTIC Marketing Hub - Marketing content portal for title companies');
    };
  }, [page.description, page.title]);

  return (
    <div style={{ paddingTop: 'var(--support-banner-height, 0px)' }} className="min-h-screen bg-gray-50">
      <header className="border-t-[6px] border-secondary-500 bg-white shadow-sm">
        <div className="mx-auto flex max-w-5xl items-center justify-between gap-4 px-4 py-4 sm:px-6">
          <Link to="/login" className="flex min-w-0 items-center gap-3 rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500">
            <img src="/ttic-logo.png" alt="Texan Title Insurance Company" className="h-12 w-12 flex-none object-contain sm:h-14 sm:w-14" />
            <div className="min-w-0">
              <p className="truncate text-sm font-bold text-primary-700 sm:text-base">TTIC Marketing Hub</p>
              <p className="hidden text-xs text-gray-500 sm:block">Marketing made easy for title companies</p>
            </div>
          </Link>
          <Link to="/login" className="inline-flex min-h-11 flex-none items-center gap-2 rounded-lg border border-gray-200 px-3 text-sm font-semibold text-gray-700 transition hover:border-primary-200 hover:bg-primary-50 hover:text-primary-700 sm:px-4">
            <ArrowLeft size={17} aria-hidden="true" />
            <span className="hidden sm:inline">Back to Marketing Hub</span>
            <span className="sm:hidden">Back</span>
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-4xl px-4 py-8 sm:px-6 sm:py-12">
        <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
          <div className="border-l-[6px] border-secondary-500 bg-primary-50 px-6 py-8 sm:px-10 sm:py-10">
            <div className="flex items-center gap-4">
              <span className="inline-flex h-12 w-12 flex-none items-center justify-center rounded-xl bg-primary-600 text-white shadow-sm">
                <Icon size={24} aria-hidden="true" />
              </span>
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.16em] text-secondary-600">TTIC Marketing Hub</p>
                <h1 className="mt-1 text-3xl font-bold tracking-tight text-primary-800 sm:text-4xl">{page.title}</h1>
              </div>
            </div>
            <p className="mt-5 max-w-2xl text-base leading-7 text-gray-600 sm:text-lg">{page.description}</p>
            {location.pathname !== '/support' && (
              <p className="mt-3 text-sm font-medium text-gray-500">Effective September 8, 2026</p>
            )}
          </div>

          <div className="space-y-8 px-6 py-8 sm:px-10 sm:py-10">
            {page.content}
          </div>
        </div>
        <PublicFooter compact />
      </main>
    </div>
  );
};

export default PublicInformation;
